/**
 * API Service for TUVISA Calendar
 * Handles all HTTP requests to the backend
 */

import { Capacitor } from '@capacitor/core';

// Use production URL for native apps, relative URL for web
const isNative = Capacitor.isNativePlatform();
const isLocalDev = !isNative && window.location.hostname === 'localhost';

export const API_BASE = isNative
    ? 'https://calendario.xerp.es/api'
    : (isLocalDev ? 'http://localhost:3000/api' : (import.meta.env.VITE_API_URL || '/api'));

const getHeaders = () => {
    const headers = {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
    };
    if (isNative) {
        headers['x-app-platform'] = 'Capacitor';
    }
    const token = localStorage.getItem('token');
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

const getFormDataHeaders = () => {
    const headers = {};
    if (isNative) {
        headers['x-app-platform'] = 'Capacitor';
    }
    const token = localStorage.getItem('token');
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

const request = async (endpoint, options = {}) => {
    let url = `${API_BASE}${endpoint}`;

    // Add timestamp to prevent caching on GET requests
    if (!options.method || options.method === 'GET') {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}_t=${Date.now()}`;
    }

    const config = {
        headers: getHeaders(),
        credentials: 'include', // Important: This allows sending cookies
        ...options,
    };

    if (config.body && typeof config.body === 'object') {
        config.body = JSON.stringify(config.body);
    }

    const response = await fetch(url, config);
    let data;
    try {
        const text = await response.text();
        try {
            data = JSON.parse(text);
        } catch (e) {
            console.error('API Error: Non-JSON response:', text);
            throw new Error('Respuesta del servidor no válida (JSON esperado)');
        }
    } catch (error) {
        console.error('API parsing error:', error);
        throw error;
    }

    if (!response.ok) {
        if (response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        throw new Error(data.error || 'Error en la petición');
    }

    return data;
};

// Auth endpoints
export const auth = {
    login: (email, password) =>
        request('/auth/login', { method: 'POST', body: { email, password } }),
    me: () => request('/auth/me'),
    register: (data) =>
        request('/auth/register', { method: 'POST', body: data }),
    getUsers: () => request('/auth/users'),
    getStats: () => request('/auth/users/stats'),
    updateUser: (id, data) =>
        request(`/auth/users/${id}`, { method: 'PUT', body: data }),
    deleteUser: (id) =>
        request(`/auth/users/${id}`, { method: 'DELETE' }),
    // Soft delete management
    getDeletedUsers: () => request('/auth/users/deleted'),
    restoreUser: (id) =>
        request(`/auth/users/${id}/restore`, { method: 'POST' }),
    purgeUser: (id) =>
        request(`/auth/users/${id}/purge`, { method: 'DELETE' }),
    getRetentionInfo: (days = 730) =>
        request(`/auth/users/retention-info?days=${days}`),
    // New endpoint to revoke sessions for a user
    revokeUserSessions: (id) =>
        request(`/auth/users/${id}/revoke-sessions`, { method: 'POST' }),
};

// Conductores endpoints
export const conductores = {
    getAll: (activo = null, tipo = null) => {
        const params = new URLSearchParams();
        if (activo !== null) params.append('activo', activo);
        if (tipo) params.append('tipo', tipo);
        const qs = params.toString();
        return request(`/conductores${qs ? `?${qs}` : ''}`);
    },
    get: (id) => request(`/conductores/${id}`),
    create: (data) =>
        request('/conductores', { method: 'POST', body: data }),
    update: (id, data) =>
        request(`/conductores/${id}`, { method: 'PUT', body: data }),
    delete: (id) =>
        request(`/conductores/${id}`, { method: 'DELETE' }),
    getAuxiliaryData: (tipo = null) => request(`/conductores/aux-data${tipo ? `?tipo=${tipo}` : ''}`),
    getWorkDaysStats: (ano = 2026) => request(`/conductores/work-days-stats?ano=${ano}`),
    // NEW: Unified calendar data - SINGLE SOURCE OF TRUTH
    getCalendario: (conductorId, ano = 2026) => request(`/conductores/${conductorId}/calendario/${ano}`),
};

// Grupos Vacaciones endpoints
export const gruposVacaciones = {
    getAll: (ano = 2026, tipo = null) => {
        const params = `ano=${ano}${tipo ? `&tipo=${tipo}` : ''}`;
        return request(`/grupos-vacaciones?${params}`);
    },
    get: (id) => request(`/grupos-vacaciones/${id}`),
    create: (data) =>
        request('/grupos-vacaciones', { method: 'POST', body: data }),
    update: (id, data) =>
        request(`/grupos-vacaciones/${id}`, { method: 'PUT', body: data }),
    delete: (id) =>
        request(`/grupos-vacaciones/${id}`, { method: 'DELETE' }),
};

// Grupos Libranzas endpoints
export const gruposLibranzas = {
    getAll: (ano = 2026, tipo = null) => {
        const params = `ano=${ano}${tipo ? `&tipo=${tipo}` : ''}`;
        return request(`/grupos-libranzas?${params}`);
    },
    get: (id) => request(`/grupos-libranzas/${id}`),
    create: (data) =>
        request('/grupos-libranzas', { method: 'POST', body: data }),
    update: (id, data) =>
        request(`/grupos-libranzas/${id}`, { method: 'PUT', body: data }),
    delete: (id) =>
        request(`/grupos-libranzas/${id}`, { method: 'DELETE' }),
};

// Lineas Fijas endpoints
export const lineasFijas = {
    getAll: (ano = new Date().getFullYear()) => request(`/lineas-fijas?ano=${ano}`),
    get: (id) => request(`/lineas-fijas/${id}`),
    create: (data) =>
        request('/lineas-fijas', { method: 'POST', body: data }),
    update: (id, data) =>
        request(`/lineas-fijas/${id}`, { method: 'PUT', body: data }),
    delete: (id) =>
        request(`/lineas-fijas/${id}`, { method: 'DELETE' }),
};

// Festivos endpoints
export const festivos = {
    getAll: (ano = 2026) => request(`/festivos?ano=${ano}`),
    get: (id) => request(`/festivos/${id}`),
    create: (data) =>
        request('/festivos', { method: 'POST', body: data }),
    update: (id, data) =>
        request(`/festivos/${id}`, { method: 'PUT', body: data }),
    delete: (id) =>
        request(`/festivos/${id}`, { method: 'DELETE' }),
};

// User endpoints (for logged-in user's own data)
export const user = {
    getConductor: () => request('/user/me/conductor'),
    getCalendario: (ano) => request(`/user/me/calendario?ano=${ano}`),
    getJornada: (fecha) => request(`/user/me/jornada/${fecha}`),
    saveJornada: (data) => request('/user/me/jornada', { method: 'POST', body: data }),
    // Avisos for user
    getAvisos: () => request('/user/me/avisos'),
    getAviso: (id) => request(`/user/me/avisos/${id}`),
    getActiveAvisoIds: () => request('/user/me/avisos/active-ids'),
    marcarAvisoLeido: (id) => request(`/user/me/avisos/${id}/leer`, { method: 'POST' }),
    // Conductor settings (groups)
    getSettings: () => request('/user/me/settings'),
    updateSettings: (data) => request('/user/me/settings', { method: 'PUT', body: data }),
    // Anexo libranzas (own days off)
    getAnexoLibranzas: (ano = 2026) => request(`/user/me/anexo-libranzas?ano=${ano}`),
    addAnexoLibranza: (fecha) => request('/user/me/anexo-libranzas', { method: 'POST', body: { fecha } }),
    deleteAnexoLibranza: (id) => request(`/user/me/anexo-libranzas/${id}`, { method: 'DELETE' }),
    // Unified History (with pagination)
    getHistorial: (limit = 20, offset = 0) => request(`/historial?limit=${limit}&offset=${offset}`),
    // UI Preferences (User persisted settings)
    getPreferences: () => request('/user/me/preferences'),
    updatePreferences: (prefs) => request('/user/me/preferences', { method: 'PUT', body: prefs }),
    // Manual Calendar (NEW)
    getManualDays: (ano) => request(`/user/me/manual-days?ano=${ano}`),
    saveManualDay: (data) => request('/user/me/manual-days', { method: 'POST', body: data }),
    saveManualBatch: (data) => request('/user/me/manual-days/bulk', { method: 'POST', body: data }),
    deleteManualDay: (fecha) => request(`/user/me/manual-days/${fecha}`, { method: 'DELETE' }),
};

// Invitation codes endpoints
export const invitations = {
    // Admin: Get all invitation codes
    getAll: () => request('/invitations'),
    // Admin: Create new invitation code
    create: (data) => request('/invitations', { method: 'POST', body: data }),
    // Admin: Delete unused invitation code
    delete: (id) => request(`/invitations/${id}`, { method: 'DELETE' }),
    // Admin: Get list of conductores for creating codes
    getConductores: () => request('/invitations/conductores'),
    // User: Redeem an invitation code
    redeem: (code) => request('/invitations/redeem', { method: 'POST', body: { code } }),
};

// Avisos endpoints
export const avisos = {
    getAll: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.estado) params.append('estado', filters.estado);
        if (filters.prioridad) params.append('prioridad', filters.prioridad);
        if (filters.limit) params.append('limit', filters.limit);
        const queryString = params.toString();
        return request(`/avisos${queryString ? `?${queryString}` : ''}`);
    },
    get: (id) => request(`/avisos/${id}`),
    getLineas: (ano = new Date().getFullYear()) => request(`/avisos/lineas?ano=${ano}`),
    create: (data) => request('/avisos', { method: 'POST', body: data }),
    update: (id, data) => request(`/avisos/${id}`, { method: 'PUT', body: data }),
    publicar: (id) => request(`/avisos/${id}/publicar`, { method: 'POST' }),
    archivar: (id) => request(`/avisos/${id}/archivar`, { method: 'POST' }),
    delete: (id) => request(`/avisos/${id}`, { method: 'DELETE' }),
    // Adjuntos - estos requieren FormData, manejo especial
    uploadAdjunto: async (avisoId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${API_BASE}/avisos/${avisoId}/adjuntos`, {
            method: 'POST',
            headers: getFormDataHeaders(),
            body: formData
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Error al subir archivo');
        }
        return response.json();
    },
    deleteAdjunto: (adjuntoId) => request(`/avisos/adjuntos/${adjuntoId}`, { method: 'DELETE' }),
    // Cleanup expired avisos attachments
    cleanup: (deleteAvisos = false) => request('/avisos/cleanup', { method: 'POST', body: { deleteAvisos } }),
};

// Motivos endpoints
export const motivos = {
    // Get all motivos (admin sees all, user sees only active)
    getAll: () => request('/motivos'),
    // Admin: Create new motivo
    create: (data) => request('/motivos', { method: 'POST', body: data }),
    // Admin: Update motivo
    update: (id, data) => request(`/motivos/${id}`, { method: 'PUT', body: data }),
    // Admin: Delete (deactivate) motivo
    delete: (id) => request(`/motivos/${id}`, { method: 'DELETE' }),
    // Get marked days for a conductor in a year
    getDias: (conductorId, year) => request(`/motivos/dias/${conductorId}/${year}`),
    // Get master list of shifts
    getTurnos: () => request('/motivos/turnos'),
    // Admin: Create new shift
    crearTurno: (data) => request('/motivos/turnos', { method: 'POST', body: data }),
    // Admin: Update shift
    actualizarTurno: (id, data) => request(`/motivos/turnos/${id}`, { method: 'PUT', body: data }),
    // Admin: Delete (deactivate/delete) shift
    eliminarTurno: (id) => request(`/motivos/turnos/${id}`, { method: 'DELETE' }),
    // Mark a day with a motivo and/or shift override
    marcarDia: (data) => request('/motivos/dias', { method: 'POST', body: data }),
    // Mark a range of days with the same motivo
    marcarPeriodo: (data) => request('/motivos/dias/periodo', { method: 'POST', body: data }),
    // Remove motivo from a day
    eliminarDia: (id) => request(`/motivos/dias/${id}`, { method: 'DELETE' }),
};

// Anexo Libranzas endpoints (individual conductor days off)
export const anexoLibranzas = {
    // Admin: Get all for a year
    getAll: (ano = 2026, tipo = null) => {
        let url = `/anexo-libranzas?ano=${ano}`;
        if (tipo) url += `&tipo=${tipo}`;
        return request(url);
    },
    // Admin: Create single entry
    create: (data) => request('/anexo-libranzas', { method: 'POST', body: data }),
    // Admin: Delete entry
    delete: (id) => request(`/anexo-libranzas/${id}`, { method: 'DELETE' }),
    // Admin: Bulk import
    bulk: (entries) => request('/anexo-libranzas/bulk', { method: 'POST', body: { entries } }),
    // Get for specific conductor
    getConductor: (conductorId, ano) => request(`/anexo-libranzas/conductor/${conductorId}/${ano}`),
    // Utility: Fix dates offset
    fixDates: () => request('/anexo-libranzas/fix-dates', { method: 'POST' }),
};

// Lineas y Servidores endpoints (for work registration)
export const lineas = {
    // Get all active lines
    getAll: (tipoDia = null) => {
        const params = tipoDia ? `?tipoDia=${tipoDia}` : '';
        return request(`/lineas${params}`);
    },
    // Get all lines including inactive (admin)
    getAllAdmin: () => request('/lineas/all'),
    // Create new line (admin)
    create: (data) => request('/lineas', { method: 'POST', body: data }),
    // Update line (admin)
    update: (id, data) => request(`/lineas/${id}`, { method: 'PUT', body: data }),
    // Delete line (admin)
    delete: (id) => request(`/lineas/${id}`, { method: 'DELETE' }),
    // Get servers for a line (optionally filtered by day type)
    getServidores: (lineaId, tipoDia = null) => {
        const params = tipoDia ? `?tipoDia=${tipoDia}` : '';
        return request(`/lineas/${lineaId}/servidores${params}`);
    },
    // Create server (admin)
    createServidor: (data) => request('/lineas/servidores', { method: 'POST', body: data }),
    // Update server (admin)
    updateServidor: (id, data) => request(`/lineas/servidores/${id}`, { method: 'PUT', body: data }),
    // Delete server (admin)
    deleteServidor: (id) => request(`/lineas/servidores/${id}`, { method: 'DELETE' }),
    // Get primas for a specific server (optionally filtered by turno)
    getServidorPrimas: (servidorId, turno = null) => {
        const params = turno ? `?turno=${turno}` : '';
        return request(`/lineas/servidores/${servidorId}/primas${params}`);
    },
    // Bulk create servers (admin)
    createServidoresBulk: (data) => request('/lineas/servidores/bulk', { method: 'POST', body: data }),
};

// Primas endpoints (bonuses for payroll)
export const primas = {
    // Get all active primas
    getAll: () => request('/primas'),
    // Get all primas including inactive (admin)
    getAllAdmin: () => request('/primas/all'),
    // Create prima (admin)
    create: (data) => request('/primas', { method: 'POST', body: data }),
    // Update prima (admin)
    update: (id, data) => request(`/primas/${id}`, { method: 'PUT', body: data }),
    // Delete prima (admin)
    delete: (id) => request(`/primas/${id}`, { method: 'DELETE' }),
};

// Audit endpoints (admin only)
export const audit = {
    // Get audit statistics
    getStats: () => request('/audit/stats'),
    // Get audit logs with pagination and filters
    getLogs: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.tipo) queryParams.append('tipo', params.tipo);
        if (params.fecha_inicio) queryParams.append('fecha_inicio', params.fecha_inicio);
        if (params.fecha_fin) queryParams.append('fecha_fin', params.fecha_fin);
        if (params.user_id) queryParams.append('user_id', params.user_id);
        return request(`/audit/logs?${queryParams.toString()}`);
    },
    // Get calendar changes with pagination and filters
    getCalendarChanges: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.conductor_id) queryParams.append('conductor_id', params.conductor_id);
        if (params.fecha_inicio) queryParams.append('fecha_inicio', params.fecha_inicio);
        if (params.fecha_fin) queryParams.append('fecha_fin', params.fecha_fin);
        if (params.created_by) queryParams.append('created_by', params.created_by);
        return request(`/audit/calendar-changes?${queryParams.toString()}`);
    },
    // Get avisos with creator info
    getAvisos: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.estado) queryParams.append('estado', params.estado);
        if (params.created_by) queryParams.append('created_by', params.created_by);
        return request(`/audit/avisos?${queryParams.toString()}`);
    },
    // Get users list for filters
    getUsers: () => request('/audit/users'),
    // Delete a calendar change entry
    deleteCalendarChange: (id) => request(`/audit/calendar-changes/${id}`, { method: 'DELETE' }),
    // Get day exchanges (intercambios) with pagination
    getIntercambios: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.conductor_id) queryParams.append('conductor_id', params.conductor_id);
        return request(`/audit/intercambios?${queryParams.toString()}`);
    },
    // Delete a day exchange (intercambio) - this also cleans up calendar entries
    deleteIntercambio: (id) => request(`/audit/intercambios/${id}`, { method: 'DELETE' }),
    // Get user activity (logins and recent actions)
    getUserActivity: (limit = 50) => request(`/audit/user-activity?limit=${limit}`),
};

// Asuntos Propios (A.P.) endpoints
export const ap = {
    // User endpoints
    getConfig: (ano) => request(`/ap/config/${ano}`),
    getBalance: (ano) => request(`/ap/balance/${ano}`),
    updateBalance: (data) => request('/ap/balance', { method: 'PUT', body: data }),
    getSolicitudes: (ano) => request(`/ap/solicitudes/${ano}`),
    solicitar: (fecha, tipo = 'ap') => request('/ap/solicitar', { method: 'POST', body: { fecha, tipo } }),
    cancelar: (id) => request(`/ap/solicitud/${id}`, { method: 'DELETE' }),
    getHistorial: (ano) => request(`/ap/historial/${ano}`),
    // NEW: User marks their request as concedido/denegado after company response
    marcarEstado: (id, estado, fecha_respuesta = null) => request(`/ap/solicitud/${id}/estado`, {
        method: 'PUT',
        body: { estado, fecha_respuesta }
    }),
    // NEW: Get complete summary for PDF generation
    getResumen: (ano) => request(`/ap/resumen/${ano}`),

    // Admin endpoints - nested under admin object
    admin: {
        getPendientes: () => request('/ap/admin/pendientes'),
        responder: (id, estado, notas) => request(`/ap/admin/responder/${id}`, {
            method: 'PUT',
            body: { estado, notas }
        }),
        getConductor: (conductorId, ano) => request(`/ap/admin/conductor/${conductorId}/${ano}`),
        getAll: (ano) => request(`/ap/admin/all/${ano}`),
    }
};

// Cambios de Días (Day Exchange) endpoints
export const cambios = {
    // Get calendar data for a month (lit up days), filtro can be 'paraMi' or 'todas'
    getCalendario: (ano, mes, filtro = 'todas') => request(`/cambios/calendario/${ano}/${mes}?filtro=${filtro}`),
    // Get my active requests
    getMisSolicitudes: () => request('/cambios/mis-solicitudes'),
    // Get requests compatible with me
    getCompatibles: () => request('/cambios/compatibles'),
    // Get all active requests
    getTodas: () => request('/cambios/todas'),
    // Get detail of a specific request
    getDetalle: (id) => request(`/cambios/${id}`),
    // Create new exchange request
    crear: (data) => request('/cambios/crear', { method: 'POST', body: data }),
    // Respond to a request
    responder: (solicitudId, ofertaElegidaId) => request(`/cambios/${solicitudId}/responder`, {
        method: 'POST',
        body: { ofertaElegidaId }
    }),
    // Accept a response
    aceptar: (solicitudId, respuestaId) => request(`/cambios/${solicitudId}/aceptar/${respuestaId}`, {
        method: 'POST'
    }),
    // Cancel my request
    cancelar: (id) => request(`/cambios/${id}`, { method: 'DELETE' }),
    // Admin: Get history
    getHistorial: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.conductorId) queryParams.append('conductorId', params.conductorId);
        if (params.desde) queryParams.append('desde', params.desde);
        if (params.hasta) queryParams.append('hasta', params.hasta);
        return request(`/cambios/admin/historial?${queryParams.toString()}`);
    },
    // Get my calendar data (work days and off days) for date picker filtering
    getMiCalendario: (ano) => request(`/cambios/mi-calendario/${ano}`)
};

// Averías (Bus Breakdowns) endpoints
export const averias = {
    // Get recent reports for a bus (last 5)
    getRecientes: (numAutobus) => request(`/averias/recientes/${numAutobus}`),
    // Create new breakdown report
    crear: (data) => request('/averias', { method: 'POST', body: data }),
    // Get all breakdowns (admin)
    getAll: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.estado) queryParams.append('estado', params.estado);
        if (params.num_autobus) queryParams.append('num_autobus', params.num_autobus);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.offset) queryParams.append('offset', params.offset);
        return request(`/averias?${queryParams.toString()}`);
    },
    // Update breakdown status (for mechanics)
    updateEstado: (id, estado, notas_mecanico) => request(`/averias/${id}/estado`, {
        method: 'PUT',
        body: { estado, notas_mecanico }
    }),
    // Update breakdown details (for owner, same day)
    update: (id, data) => request(`/averias/${id}`, {
        method: 'PUT',
        body: data
    }),
    // Delete breakdown (for owner, same day)
    delete: (id) => request(`/averias/${id}`, { method: 'DELETE' })
};

// Recordatorios endpoints (day reminders)
export const recordatorios = {
    // Get reminder for a specific date
    get: (fecha) => request(`/recordatorios/${fecha}`),
    // Create or update reminder
    save: (fecha, hora, mensaje) => request('/recordatorios', {
        method: 'POST',
        body: { fecha, hora, mensaje }
    }),
    // Delete reminder for a date
    delete: (fecha) => request(`/recordatorios/${fecha}`, { method: 'DELETE' })
};

// Roles y Permisos endpoints (admin only)
export const roles = {
    // Get all roles
    getAll: () => request('/roles'),
    // Get single role with permissions
    get: (id) => request(`/roles/${id}`),
    // Create role
    create: (data) => request('/roles', { method: 'POST', body: data }),
    // Update role
    update: (id, data) => request(`/roles/${id}`, { method: 'PUT', body: data }),
    // Delete role
    delete: (id) => request(`/roles/${id}`, { method: 'DELETE' }),
    // Get menu items as tree
    getMenuTree: () => request('/roles/menu-items/tree'),
    // Get menu items flat
    getMenuFlat: () => request('/roles/menu-items/flat'),
    // Get all permissions
    getPermisos: () => request('/roles/permisos/all'),
    // Update role permissions
    updatePermisos: (roleId, permisos) => request(`/roles/${roleId}/permisos`, {
        method: 'PUT',
        body: { permisos }
    }),
    // Get user permissions
    getUserPermisos: (userId) => request(`/roles/users/${userId}/permisos`),
    // Update user extra permissions
    updateUserExtras: (userId, permisos) => request(`/roles/users/${userId}/permisos-extra`, {
        method: 'PUT',
        body: { permisos }
    }),
    // Get current user's permissions
    getMyPermisos: () => request('/auth/me/permisos'),
};

// Accidentes (Accident Reports) endpoints
export const accidentes = {
    // Create new accident report
    crear: (data) => request('/accidentes', { method: 'POST', body: data }),
    // Get my reports
    getMisPartes: () => request('/accidentes/mis-partes'),
    // Get report details
    get: (id) => request(`/accidentes/${id}`),
    // Get all reports (admin)
    getAll: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.conductor_id) queryParams.append('conductor_id', params.conductor_id);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.offset) queryParams.append('offset', params.offset);
        return request(`/accidentes?${queryParams.toString()}`);
    },
    // Upload photos (requires FormData)
    uploadFotos: async (accidenteId, files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('fotos', file));
        const response = await fetch(`${API_BASE}/accidentes/${accidenteId}/fotos`, {
            method: 'POST',
            headers: getFormDataHeaders(),
            body: formData
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Error al subir fotos');
        }
        return response.json();
    }
};

// Gautxori (night service) endpoints
export const gautxori = {
    // Get all Gautxori assignments, optionally filtered by year and conductor
    getAll: (ano = 2026, conductorId = null) => {
        const params = new URLSearchParams({ ano });
        if (conductorId) params.append('conductor_id', conductorId);
        return request(`/gautxori?${params.toString()}`);
    },
    // Get for a specific conductor and year
    getConductor: (conductorId, ano = 2026) => request(`/gautxori/conductor/${conductorId}/${ano}`),
    // Create new assignment
    create: (data) => request('/gautxori', { method: 'POST', body: data }),
    // Update assignment (set compensation date)
    update: (id, data) => request(`/gautxori/${id}`, { method: 'PUT', body: data }),
    // Delete assignment
    delete: (id) => request(`/gautxori/${id}`, { method: 'DELETE' }),
};

// Admin endpoints
export const admin = {
    // Get global day status for all conductors on a specific date
    getGlobalDayStatus: (date = null, tipo = null) => {
        const params = new URLSearchParams();
        if (date) params.append('date', date);
        if (tipo) params.append('tipo', tipo);
        const qs = params.toString();
        return request(`/admin/global-day-status${qs ? `?${qs}` : ''}`);
    },
    // Get business adoption metrics
    getMetricasImpacto: () => request('/admin/metricas-impacto'),
};

// Notas endpoints
export const notas = {
    // Get notes for a month
    getMes: (conductorId, ano, mes) => request(`/notas/mes/${conductorId}/${ano}/${mes}`),
    // Get notes for a specific day
    getDia: (conductorId, fecha) => request(`/notas/dia/${conductorId}/${fecha}`),
    // Create note
    create: (data) => request('/notas', { method: 'POST', body: data }),
    // Update note
    update: (id, data) => request(`/notas/${id}`, { method: 'PUT', body: data }),
    // Delete note
    delete: (id) => request(`/notas/${id}`, { method: 'DELETE' }),
};

// Bolsa de Días endpoints
export const bolsa = {
    get: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.conductor_id) queryParams.append('conductor_id', params.conductor_id);
        return request(`/bolsa-dias?${queryParams.toString()}`);
    },
    getDeudores: () => request('/bolsa-dias/deudores')
};

// Horas Sindicales endpoints
export const horasSindicales = {
    getSaldo: (ano, conductorId = null) => {
        const params = new URLSearchParams({ ano });
        if (conductorId) params.append('conductor_id', conductorId);
        return request(`/horas-sindicales/saldo?${params.toString()}`);
    },
    getHistorial: (ano) => request(`/horas-sindicales/historial?ano=${ano}`),
    getConfig: (ano) => request(`/horas-sindicales/config/${ano}`),
    updateConfig: (ano, data) => request(`/horas-sindicales/config/${ano}`, { method: 'PUT', body: data }),
};

// OPE Endpoints
export const ope = {
    getTemas() {
        return request('/ope/temas');
    },
    getSecciones(temaId) {
        return request(`/ope/temas/${temaId}/secciones`);
    },
    getPreguntasTema(temaId) {
        return request(`/ope/preguntas/tema/${temaId}`);
    },
    responder(data) {
        return request('/ope/responder', { method: 'POST', body: JSON.stringify(data) });
    },
    getStats() {
        return request('/ope/stats');
    },
    getRanking() {
        return request('/ope/ranking');
    },
    // ── Reto Diario endpoints ────────────────────
    getRetoDiario() {
        return request('/ope/reto-diario');
    },
    completarRetoDiario(data) {
        return request('/ope/reto-diario/completar', { method: 'POST', body: data });
    },
    getRetoDiarioRanking() {
        return request('/ope/reto-diario/ranking');
    },
    // ── Admin endpoints ──────────────────────────
    adminGetTemas() {
        return request('/ope/admin/temas');
    },
    adminCreateTema(data) {
        return request('/ope/admin/temas', { method: 'POST', body: data });
    },
    adminUpdateTema(id, data) {
        return request(`/ope/admin/temas/${id}`, { method: 'PUT', body: data });
    },
    adminDeleteTema(id) {
        return request(`/ope/admin/temas/${id}`, { method: 'DELETE' });
    },
    async adminUploadPdf(temaId, file) {
        const formData = new FormData();
        formData.append('pdf', file);
        const response = await fetch(`${API_BASE}/ope/admin/temas/${temaId}/upload-pdf`, {
            method: 'POST',
            headers: getFormDataHeaders(),
            body: formData
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Error al subir PDF');
        }
        return response.json();
    },
    adminGetPromptResumen(temaId) {
        return request(`/ope/admin/temas/${temaId}/prompt-resumen`);
    },
    adminGenerateSummary(temaId, prompt) {
        return request(`/ope/admin/temas/${temaId}/generate-summary`, { method: 'POST', body: { prompt } });
    },
    adminGetResumen(temaId) {
        return request(`/ope/admin/temas/${temaId}/resumen`);
    },
    adminGetAudioConfig(temaId) {
        return request(`/ope/admin/temas/${temaId}/audio-config`);
    },
    adminSaveAudioConfig(temaId, config) {
        return request(`/ope/admin/temas/${temaId}/audio-config`, { method: 'PUT', body: config });
    },
    adminTestAudio(temaId, payload) {
        return request(`/ope/admin/temas/${temaId}/test-audio`, { method: 'POST', body: payload });
    },
    adminGenerateAudio(temaId, config, options = {}) {
        return request(`/ope/admin/temas/${temaId}/generate-audio`, { method: 'POST', body: config, ...options });
    },
    adminGetPromptPreguntas(temaId) {
        return request(`/ope/admin/temas/${temaId}/prompt-preguntas`);
    },
    adminGenerateQuestions(temaId, prompt, cantidad) {
        return request(`/ope/admin/temas/${temaId}/generate-questions`, { method: 'POST', body: { prompt, cantidad } });
    },
    adminGetPreguntas(temaId) {
        return request(`/ope/admin/temas/${temaId}/preguntas`);
    },
    adminUpdatePregunta(id, data) {
        return request(`/ope/admin/preguntas/${id}`, { method: 'PUT', body: data });
    },
    adminCambiarEstado(id, estado) {
        return request(`/ope/admin/preguntas/${id}/estado`, { method: 'PUT', body: { estado } });
    },
    adminPublicarTodas(temaId) {
        return request(`/ope/admin/preguntas/publicar-todas/${temaId}`, { method: 'POST' });
    },
    adminDeletePregunta(id) {
        return request(`/ope/admin/preguntas/${id}`, { method: 'DELETE' });
    },
    adminGetRetoConfig() {
        return request('/ope/admin/reto-config');
    },
    adminUpdateRetoConfig(data) {
        return request('/ope/admin/reto-config', { method: 'PUT', body: data });
    },
    adminGetRetoStats() {
        return request('/ope/admin/reto-stats');
    }
};


export default {
    auth,
    conductores,
    gruposVacaciones,
    gruposLibranzas,
    lineasFijas,
    festivos,
    user,
    invitations,
    avisos,
    motivos,
    anexoLibranzas,
    lineas,
    primas,
    audit,
    ap,
    cambios,
    averias,
    recordatorios,
    roles,
    accidentes,
    accidentes,
    admin,
    gautxori,
    ope,
    notas,
    bolsa,
    horasSindicales,
    // Helper methods
    getUri: () => {
        return API_BASE.endsWith('/api')
            ? API_BASE.slice(0, -4)
            : API_BASE;
    },
    get: (endpoint) => request(endpoint),
    post: (endpoint, data) => request(endpoint, { method: 'POST', body: data }),
    put: (endpoint, data) => request(endpoint, { method: 'PUT', body: data }),
    delete: (endpoint) => request(endpoint, { method: 'DELETE' }),
    postFormData: async (endpoint, formData) => {
        let url = `${API_BASE}${endpoint}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: getFormDataHeaders(),
            body: formData
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Error en la petición');
        }
        return response.json();
    }
};

